import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { createClientNews, getClientNewsById, updateClientNews } from 'app/api/clientNewsApi';
import { Editor } from 'primereact/editor';
import { InputText } from 'primereact/inputtext';
import { FileUpload } from 'primereact/fileupload';
import { Checkbox } from 'primereact/checkbox';

const CreateClientNews: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const fileUploadRef = useRef(null);
  const [image, setImage] = useState(null);

  useEffect(() => {
    if (id) {
      getClientNewsById(id)
        .then(response => {
          if (response.data) {
            setData(response.data);
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const onSubmit = () => {
    let call;

    if (id) {
      call = updateClientNews(id, data, image);
    } else {
      call = createClientNews(data, image);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Novinka úspešne uložená.');
        setData(response.data);
        navigate(`/client-news?p=${tempParameters}`);
      })
      .catch(error => {
        console.error('Error creating agent:', error);
        showError('Chyba', error.response?.data?.detail);
      })
      .finally(() => setIsSaving(false));
  };

  const onCancel = () => {
    navigate(`/client-news?p=${tempParameters}`);
  };

  const handleSelect = e => {
    setImage(e.files[0]);
  };

  const renderEditorHeader = () => {
    return (
      <span className="ql-formats">
        <button className="ql-header ql-picker"></button>
        <button className="ql-bold" aria-label="Bold"></button>
        <button className="ql-italic" aria-label="Italic"></button>
        <button className="ql-underline" aria-label="Underline"></button>
        <button className="ql-list" value="ordered" aria-label="Ordered list"></button>
        <button className="ql-align ql-picker ql-icon-picker ql-expanded" value="ordered" aria-label="Ordered list"></button>
        <button className="ql-link" value="ordered" aria-label="Insert link"></button>
      </span>
    );
  };

  const editorHeader = renderEditorHeader();

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť klientské novinky' : 'Vytvoriť klientskú novinku'}</h2>
          <div className="grid">
            <div className="col-12">
              <div className="p-field">
                <label htmlFor="showToClients">Ukázať klientom</label>
                <Checkbox
                  name="showToClients"
                  onChange={e => {
                    setData(prevState => ({
                      ...prevState,
                      showToClients: e.checked ?? '',
                    }));
                  }}
                  checked={data?.showToClients}
                ></Checkbox>
              </div>
            </div>
            <div className="col-12 md:col-6">
              <div className="p-field">
                <label htmlFor="title">Názov novinky</label>
                <InputText
                  value={data?.title || ''}
                  onChange={e => {
                    setData(prevState => ({
                      ...prevState,
                      title: e.target.value ?? '',
                    }));
                  }}
                  placeholder="Názov novinky"
                />
              </div>
            </div>
            <div className="col-12">
              <label htmlFor="message">Správa</label>
              <Editor
                id="message"
                style={{ height: '250px' }}
                className="mt-3"
                value={data?.message}
                headerTemplate={editorHeader}
                onTextChange={e =>
                  setData(prevState => ({
                    ...prevState,
                    message: e.htmlValue ?? '',
                  }))
                }
              ></Editor>
            </div>
          </div>
          <div>
            <FileUpload
              ref={fileUploadRef}
              name="attachments"
              multiple
              mode="advanced"
              accept=".png,.jpg,.jpeg"
              customUpload
              auto={false} // don't auto-upload
              chooseLabel="Pridať obrázok"
              cancelLabel="Zrušiť"
              uploadLabel={null}
              onSelect={handleSelect}
              chooseOptions={{ icon: 'pi pi-paperclip' }}
            />
          </div>
          <div>
            {data?.imagePath && (
              <div>
                <label htmlFor="image">Uložený obrázok</label>
                <img
                  id="image"
                  className="w-40 sm:w-60rem xl:w-50rem shadow-2 block xl:block mx-auto border-round"
                  src={`https://system.nfagroup.eu/api/client-news/image/${data.imagePath}`}
                  width="300"
                  alt={data.imagePath}
                />
              </div>
            )}
          </div>
          <Button
            style={{ backgroundColor: '#e31818' }}
            type="button"
            onClick={onSubmit}
            label="Uložiť"
            icon="pi pi-check"
            className="p-mt-3"
            disabled={isSaving}
          />
          <Button
            type="button"
            style={{ marginTop: '10px', backgroundColor: 'gray' }}
            label="Naspäť"
            icon="pi pi-arrow-left"
            className="p-button-secondary"
            onClick={() => navigate(`/client-news?p=${tempParameters}`)}
          />
        </>
      )}
    </div>
  );
};

export default CreateClientNews;
